/**
 * ErrorScenarioView — binds a content-only {@link ErrorScenario} to the live
 * app: it picks the chrome from the auth session, injects the real
 * {@link GlobalNavBar} for signed-in members, maps the scenario's action set to
 * concrete buttons, and sets the document title. The presentational work lives
 * in {@link ErrorSurface}; this is the thin app-aware adapter both the catch-all
 * 404 and the `/error/:code` route render.
 */
import { useEffect } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { GlobalNavBar } from '@/components/v2/layout/GlobalNavBar';

import {
  ErrorSurface,
  type ErrorSurfaceAction,
  type ErrorStatusLevel,
  type ErrorSurfaceTopbar,
} from './ErrorSurface';
import { ErrorVoiceControl } from './ErrorVoiceControl';
import type { ErrorActionSet, ErrorScenario } from './errorScenarios';

/** The two static action sets from the design — Back/Home, or a single sign-in. */
const ACTION_SETS: Record<ErrorActionSet, ErrorSurfaceAction[]> = {
  backHome: [
    { label: 'Back', kind: 'ghost', icon: 'back', onClick: () => window.history.back() },
    { label: 'Home', kind: 'primary', icon: 'home', href: '/' },
  ],
  login: [{ label: 'Go to login', kind: 'primary', icon: 'login', href: '/login' }],
};

export interface ErrorScenarioViewProps {
  scenario: ErrorScenario;
  /**
   * Override the scenario's default action set. Used when the caller owns an
   * action the static sets can't express — e.g. the app ErrorBoundary injecting
   * an in-place "Try again" (its `handleRetry`) that resets the boundary rather
   * than navigating. Omit to use the scenario's `actions` set.
   */
  actions?: ErrorSurfaceAction[];
  /**
   * Chrome mode. `'auto'` (default) picks the real nav for signed-in members and
   * the wordmark for guests, and shows the voice control for members. `'wordmark'`
   * forces the **dependency-free** wordmark chrome and omits the voice control —
   * used by the app ErrorBoundary, whose fallback must not pull app-data hooks
   * (`GlobalNavBar`'s projects/orgs queries, `useProfileSettings`). If the caught
   * crash originates in that shared data layer, an `'auto'` fallback would
   * re-throw while rendering; React sends that to the *parent* boundary, and the
   * top-level `App.tsx` boundary has none — degrading to a white screen. Forcing
   * wordmark removes the re-throw surface so the crash page always renders.
   */
  chrome?: 'auto' | 'wordmark';
}

export function ErrorScenarioView({ scenario, actions, chrome = 'auto' }: ErrorScenarioViewProps) {
  const { isAuthenticated } = useAuth();
  const forceWordmark = chrome === 'wordmark';

  // The unauthenticated surface (and the ErrorBoundary's forced-wordmark mode) is
  // always the minimal wordmark. Otherwise: the real nav for signed-in members,
  // the wordmark for guests.
  const topbar: ErrorSurfaceTopbar = forceWordmark
    ? 'wordmark'
    : scenario.forceTopbar ?? (isAuthenticated ? 'nav' : 'wordmark');

  // The wordmark status pill mirrors the incident level, if any.
  const status: ErrorStatusLevel = scenario.banner ? scenario.banner.level : 'operational';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `Anaka — ${scenario.code} · ${scenario.headline}`;
    return () => {
      document.title = previousTitle;
    };
  }, [scenario.code, scenario.headline]);

  return (
    <ErrorSurface
      code={scenario.code}
      headline={scenario.headline}
      message={scenario.message}
      topbar={topbar}
      nav={topbar === 'nav' ? <GlobalNavBar /> : undefined}
      banner={scenario.banner ?? null}
      status={status}
      hideStatus={scenario.id === 'unauthenticated'}
      actions={actions ?? ACTION_SETS[scenario.actions]}
      motif={scenario.motif}
      // Voice playback only applies to signed-in users (guests have no profile
      // to read a voice preference from), and is suppressed in forced-wordmark
      // mode so the ErrorBoundary fallback stays free of the profile data hook.
      messageAside={
        !forceWordmark && isAuthenticated ? (
          <ErrorVoiceControl scenarioId={scenario.id} message={scenario.message} />
        ) : undefined
      }
    />
  );
}

export default ErrorScenarioView;
